<script setup>
import { ArrowLongDownIcon, ArrowLongUpIcon } from '@heroicons/vue/20/solid';
import { useIntersectionObserver } from '@vueuse/core';
import { ref } from 'vue';
import ProgramItem from './ProgramItem.vue';
import { useGetLocalizedDateTime } from '@/Composables/localizedDateTime.js';

const emit = defineEmits(['nextDateClicked', 'previousDateClicked']);

const stickyElement = ref(null);
const isSticky = ref(false);

useIntersectionObserver(
    stickyElement,
    ([{ isIntersecting }]) => {
        isSticky.value = !isIntersecting;
    }, {
        threshold: 1,
    },
);

defineProps({
    programItems: {
        type: Array,
        required: true,
    },
    startDate: {
        type: String,
        required: true,
    },
    showNextButton: {
        type: Boolean,
        required: true,
    },
    showPreviousButton: {
        type: Boolean,
        required: true,
    },
});
</script>

<template>
    <div
        :id="`date-${useGetLocalizedDateTime(startDate, 'yyyyMMdd')}`"
        class="scroll-mt-5"
    >
        <div
            ref="stickyElement"
            class="z-[1] flex items-center justify-between border border-gray-200 border-b-0 md:px-6 pb-4"
            :class="[
                'sticky md:static -top-px md:top-auto pt-[calc(1rem+1px)] md:pt-4 transition duration-150 md:shadow-none',
                isSticky ? '-mx-4 md:mx-0 px-8 md:px-4 shadow-md supports-[backdrop-filter]:backdrop-blur supports-[backdrop-filter]:bg-white/75': 'bg-white px-4'
            ]"
        >
            <div class="text-xl md:text-2xl font-semibold text-deep-teal-700">
                {{ useGetLocalizedDateTime(startDate, 'P') }}
            </div>

            <!-- next/prev buttons -->
            <div class="inline-flex items-center space-x-2">
                <button
                    v-if="showNextButton"
                    :class="{ 'hidden md:block': showPreviousButton }"
                    @click="emit('nextDateClicked', { currentId: `date-${useGetLocalizedDateTime(startDate, 'yyyyMMdd')}` })"
                >
                    <span class="sr-only">
                        {{ $t('eventDetails.actions.jumpToNextDate') }}
                    </span>

                    <ArrowLongDownIcon
                        class="h-6 w-6 text-gray-400 hover:text-gray-500"
                        aria-hidden="true"
                    />
                </button>

                <button
                    v-if="showPreviousButton"
                    @click="emit('previousDateClicked', { currentId: `date-${useGetLocalizedDateTime(startDate, 'yyyyMMdd')}` })"
                >
                    <span class="sr-only">
                        {{ $t('eventDetails.actions.jumpToPreviousDate') }}
                    </span>

                    <ArrowLongUpIcon
                        class="h-6 w-6 text-gray-400 hover:text-gray-500"
                        aria-hidden="true"
                    />
                </button>
            </div>
        </div>

        <div>
            <ProgramItem
                v-for="(item, index) in programItems"
                :key="index"
                :item
            />
        </div>
    </div>
</template>
